import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const STATUS_TONE = {
  Open: "bg-slate-700/40 text-slate-300",
  Closing: "bg-amber-500/15 text-amber-400",
  Closed: "bg-rose-500/15 text-rose-400",
};

export default function AccountingPeriods() {
  const { can } = useAuth();
  const canPrepare = can("accounting.period.manage");
  const canApprove = can("accounting.period.approve");
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [checklist, setChecklist] = useState({});

  const loadStatus = useCallback(async () => {
    try {
      const r = (await api.get(`/finance/closing/status?year=${year}&month=${month}`)).data;
      setData(r);
      setChecklist(r.period?.checklist || {});
    } catch (e) { toast.error(apiError(e)); }
  }, [year, month]);
  const loadPeriods = useCallback(async () => {
    try { setPeriods((await api.get("/finance/closing/periods")).data); } catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadPeriods(); }, [loadPeriods]);

  const refresh = () => { loadStatus(); loadPeriods(); };
  const requestClose = async () => {
    try { await api.post("/finance/closing/request", { year, month, checklist }); toast.success("Pengajuan tutup buku terkirim ke Owner"); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const approve = async () => { try { await api.post("/finance/closing/approve", { year, month }); toast.success("Periode ditutup (disetujui Owner)"); refresh(); } catch (e) { toast.error(apiError(e)); } };
  const reject = async () => { const reason = prompt("Alasan menolak pengajuan (opsional):") ?? ""; try { await api.post("/finance/closing/reject", { year, month, reason }); toast.success("Pengajuan ditolak"); refresh(); } catch (e) { toast.error(apiError(e)); } };
  const reopen = async () => { const reason = prompt("Alasan buka kembali (WAJIB):"); if (!reason || !reason.trim()) return toast.error("Alasan wajib diisi"); try { await api.post("/finance/closing/reopen", { year, month, reason }); toast.success("Periode dibuka kembali"); refresh(); } catch (e) { toast.error(apiError(e)); } };

  const st = data?.period?.status || "Open";
  const pc = data?.precheck;
  const sevTone = (i) => i.ok ? "text-emerald-400" : i.severity === "BLOCKER" ? "text-rose-400" : "text-amber-400";

  return (
    <div className="p-6" data-testid="periods-page">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold text-white">Tutup Buku (Period Closing)</h1>
        <p className="text-slate-400 text-sm">Accounting menyiapkan & mengajukan tutup buku; Owner memberi persetujuan final. Reopen wajib alasan & tercatat audit.</p>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div><label className="text-xs text-slate-400">Tahun</label>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="period-year" className="block bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white">
            {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => <option key={y} value={y}>{y}</option>)}
          </select></div>
        <div><label className="text-xs text-slate-400">Bulan</label>
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="period-month" className="block bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white">
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select></div>
        <span className={`text-xs px-2 py-1 rounded ${STATUS_TONE[st]}`} data-testid="period-status">{st}</span>
      </div>

      {pc && (
        <Card className="p-4 bg-slate-900 border-slate-800 mb-4" data-testid="precheck-card">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-white font-medium">Pre-Check Tutup Buku</h3>
            <span className={`text-xs px-2 py-1 rounded ${pc.can_close ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`}>
              {pc.can_close ? "Siap ditutup" : `${pc.blocker_count} blocker`}{pc.warning_count ? ` · ${pc.warning_count} warning` : ""}
            </span>
          </div>
          <div className="space-y-1">
            {pc.items.map((i) => (
              <div key={i.code} data-testid={`precheck-${i.code}`} className="flex justify-between text-sm border-b border-slate-800/60 py-1">
                <span className="text-slate-300">{i.ok ? "✓" : i.severity === "BLOCKER" ? "✕" : "!"} {i.label}</span>
                <span className={sevTone(i)}>{i.detail}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {data && (st === "Open" || st === "Closing") && (
        <Card className="p-4 bg-slate-900 border-slate-800 mb-4" data-testid="checklist-card">
          <h3 className="text-white font-medium mb-2">Checklist Tutup Buku (Accounting)</h3>
          <div className="space-y-2">
            {data.manual_checklist.map((c) => (
              <label key={c.key} className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" data-testid={`check-${c.key}`} checked={!!checklist[c.key]}
                  disabled={st === "Closing" || !canPrepare}
                  onChange={(e) => setChecklist({ ...checklist, [c.key]: e.target.checked })} />
                {c.label}
              </label>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            {st === "Open" && canPrepare && <Button data-testid="request-close-btn" onClick={requestClose} disabled={!pc?.can_close} className="bg-amber-600 hover:bg-amber-500 disabled:opacity-40">Ajukan Tutup Buku</Button>}
            {st === "Closing" && canApprove && <Button data-testid="approve-close-btn" onClick={approve} className="bg-emerald-600 hover:bg-emerald-500">Setujui & Tutup (Owner)</Button>}
            {st === "Closing" && canApprove && <Button data-testid="reject-close-btn" onClick={reject} className="bg-rose-600 hover:bg-rose-500">Tolak Pengajuan</Button>}
            {st === "Closing" && <span className="text-xs text-slate-500 self-center">Diajukan oleh {data.period.requested_by} · menunggu persetujuan Owner</span>}
          </div>
        </Card>
      )}

      {st === "Closed" && (
        <Card className="p-4 bg-slate-900 border-slate-800 mb-4">
          <div className="text-sm text-slate-300">Periode <b>{MONTHS[month - 1]} {year}</b> telah ditutup oleh {data?.period?.closed_by}.</div>
          {canApprove && <Button data-testid="reopen-btn" onClick={reopen} className="mt-3 bg-amber-600 hover:bg-amber-500">Buka Kembali (Owner)</Button>}
        </Card>
      )}

      <Card className="p-0 bg-slate-900 border-slate-800 overflow-x-auto">
        <table className="w-full text-sm text-slate-300">
          <thead><tr className="text-left text-slate-500 border-b border-slate-800"><th className="p-3">Periode</th><th>Status</th><th>Diajukan</th><th>Ditutup Oleh</th><th>Alasan Reopen</th></tr></thead>
          <tbody>
            {periods.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-slate-500">Belum ada riwayat periode.</td></tr>}
            {periods.map((p) => (
              <tr key={p.ym} data-testid={`period-row-${p.ym}`} className="border-b border-slate-800">
                <td className="p-3">{MONTHS[p.month - 1]} {p.year}</td>
                <td><span className={`text-xs px-2 py-1 rounded ${STATUS_TONE[p.status] || "bg-slate-700/40"}`}>{p.status}</span></td>
                <td className="text-xs text-slate-500">{p.requested_by || "-"}</td>
                <td className="text-xs text-slate-500">{p.closed_by || "-"}</td>
                <td className="text-xs text-slate-500">{p.reopen_reason || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
